import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrueImpactDropdownRow from './TrueImpactDropdownRow';
import FormGroup from '@mui/material/FormGroup/FormGroup';
import FeedbackDashboardFilterSelect from '../../components/Feedback/FeedbackDashboardFilterSelect';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { MenuItem, Select, SelectProps } from '@mui/material';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';

const StyledSelect = styled(Select)<{ borderColor: keyof typeof COLORS }>`
  background-color: ${COLORS.whiteBase};
  .MuiSelect-root.MuiSelect-select {
    padding-top: ${SPACERS.small};
    padding-bottom: ${SPACERS.small};
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ borderColor }) => COLORS[borderColor]};
  }
`;

const optionStyles: React.CSSProperties = {
  fontSize: 16,
  padding: SPACERS.small,
};

interface TrueImpactDropdownBaseProps<T> {
  value?: T;
  options: T[];
  native?: boolean;
  borderColor?: keyof typeof COLORS;
  icon?: IconProp | IconDefinition;
  getId: (value: T) => string | number;
  getLabel: (value: T) => string | number | JSX.Element;
  minWidth?: string;
  className?: string;
}

type DropdownProps<T> = TrueImpactDropdownBaseProps<T> &
  (
    | {
        onClear: undefined;
        onChange: (value: T, index: number) => unknown;
      }
    | {
        onClear: () => unknown;
        onChange: (value?: T, index?: number) => unknown;
      }
  );

type TrueImpactDropdownProps<T> =
  | (TrueImpactDropdownBaseProps<T> & {
      name: string;
      native: true;
      disabled?: boolean;
      onClear?: undefined;
      getSelectorValues?: undefined;
      onChange: (value: T) => unknown;
      renderValue?: (value: T) => React.ReactNode;
      getItemDisability: ((item: T) => boolean) | undefined;
    })
  | (DropdownProps<T> & {
      native: false;
      name?: undefined;
      disabled?: boolean;
      getItemDisability?: undefined;
      renderValue?: undefined;
      getSelectorValues: (
        options: T[],
        value: T | undefined,
      ) => {
        label: JSX.Element | string | number;
        isAnySelected: boolean;
      };
    });

const TrueImpactDropdown = <T,>({
  value,
  options,
  native,
  name,
  icon,
  minWidth,
  disabled,
  borderColor = 'grayBase',
  getId,
  getLabel,
  renderValue,
  getSelectorValues,
  getItemDisability,
  onChange,
  onClear,
  className,
}: TrueImpactDropdownProps<T>) => {
  const handleClear = useCallback(() => {
    if (onClear) {
      onChange(undefined, undefined);
      onClear();
    }
  }, [onClear]);

  if (native) {
    return (
      <StyledSelect
        name={name}
        aria-label={name}
        required
        disabled={disabled}
        fullWidth
        displayEmpty
        value={value || ''} // empty string to show null text
        variant="outlined"
        className={className}
        renderValue={renderValue as SelectProps['renderValue'] | undefined}
        borderColor={borderColor}
        onChange={e => onChange(e.target.value as T)}
      >
        <MenuItem value="" style={optionStyles}>
          {name}
        </MenuItem>
        {options.map(p => (
          <MenuItem
            key={getId(p)}
            value={p as string}
            style={optionStyles}
            disabled={getItemDisability && getItemDisability(p)}
          >
            {getLabel(p)}
          </MenuItem>
        ))}
      </StyledSelect>
    );
  }

  const { label, isAnySelected } = getSelectorValues(options, value);

  const Component = (handleClose: () => unknown) => (
    <FormGroup>
      {options.map((d, i) => {
        const id = getId(d);
        return (
          <TrueImpactDropdownRow
            key={id}
            selected={value ? id === getId(value) : false}
            text={getLabel(d)}
            onClick={() => {
              onChange(d, i);
              handleClose();
            }}
          />
        );
      })}
    </FormGroup>
  );

  return (
    <FeedbackDashboardFilterSelect
      type="radio"
      icon={icon}
      onClose={undefined}
      minWidth={minWidth}
      disabled={disabled}
      borderColor={borderColor}
      selectorLabel={value ? getLabel(value) : label}
      className={className}
      Component={Component}
      clearButtonText="Clear"
      disableClearButton={!isAnySelected}
      onClearAll={onClear && handleClear}
    />
  );
};

export default TrueImpactDropdown;
